/**
 * Application entry point.
 */

import React, { Suspense, lazy, useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { useNavVariant } from "./utils/navigation/useNavVariant";

import { ProtectedRoute } from "./components/authentication/ProtectedRoute";

import NavBar from "./components/navigation/NavBar";
import MDNavBar from "./components/navigation/MDNavBar";
import ForgotPasswordRequest from "./components/authentication/ForgotPasswordRequest";
import ResetPassword from "./components/authentication/ResetPassword";

import { useAuth } from "./context/AuthContext";
import { useClinicFormComponent } from "./forms/useClinicFormComponent";
import AdminPage from "./pages/AdminPage";

/* ------------------------------------------------------------------ */
/* Lazy-loaded pages (code-splitting)                                 */
/* ------------------------------------------------------------------ */
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const EcrfReviewPage = lazy(() => import("./pages/EcrfReviewPage"));

/* ------------------------------------------------------------------ */

function ClinicFormRoute() {
  // Choose the right component based on claims.clinic
  const { Component } = useClinicFormComponent();
  return <Component />;
}

export default function App() {
  const { user, claims } = useAuth();
  const role = claims?.role;
  const isAdmin =
    ["monitor", "principal_investigator", "chief_investigator"].includes(
      role
    ) || user?.email == "bdruce@electronrx.com";
  // const isAdmin = true;

  const navVariant = useNavVariant(); // 'main' | 'landing'

  return (
    <div className="h-[100dvh] flex flex-col typography relative ">
      {navVariant === "main" ? <NavBar /> : <MDNavBar />}

      <div className="h-full flex md:items-start md:justify-center px-4 pb-4 overflow-y-scroll">
        <Suspense fallback={<div className="m-auto">Loading…</div>}>
          <Routes>
            {/* ---------- Public routes ---------- */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/auth/*" element={<AuthPage />} />
            <Route
              path="/auth/forgot-password"
              element={<ForgotPasswordRequest />}
            />
            <Route path="/auth/reset-password" element={<ResetPassword />} />

            {/* ---------- Protected routes ---------- */}
            <Route
              path="/form"
              element={
                <ProtectedRoute>
                  {/* This renders the clinic-specific form component */}
                  <ClinicFormRoute />
                </ProtectedRoute>
              }
            />

            <Route
              path="/records"
              element={
                <ProtectedRoute>
                  <EcrfReviewPage />
                </ProtectedRoute>
              }
            />

            <Route
              path="/admin"
              element={
                <ProtectedRoute>
                  {isAdmin ? <AdminPage /> : <Navigate to="/form" replace />}
                </ProtectedRoute>
              }
            />

            {/* ---------- Fallback ---------- */}
            <Route path="*" element={<Navigate to={"/"} replace />} />
          </Routes>
        </Suspense>
      </div>

      <div className="muted-text flex flex-row justify-between px-1 pb-1">
        <p>© July 2025, electronRx Ltd</p>
        <p>Cambridge, UK</p>
      </div>
    </div>
  );
}
